import { RecordOf } from 'immutable';
import { TemplateEditorStateExport } from 'components/VideoTemplateEditor';
import { ClipAudioStepProps } from 'containers/ProjectWizards/ClipAudioStep';

import { CreateCreationAssetArgs } from 'redux/middleware/api/creation-service/types';
import { Action } from 'redux/types';
import {
  AspectRatioDimensions,
  AudioClipOffsets,
  CaptionsOverride,
  FrameSize,
  PodcastIdentifier,
  ProjectCreationMethod,
  RequestStatus,
  Soundwave,
} from 'types';
import { Type } from './action-types';

type Transcription = ClipAudioStepProps['transcription'] & {
  transcriptUrl?: string;
  override?: CaptionsOverride;
};

interface Audio {
  source: string | File;
}

interface ClippedAudio extends AudioClipOffsets {
  entireAudioInstanceId: number;
}

export function isClippedAudio(arg: any): arg is ClippedAudio {
  return !!arg?.entireAudioInstanceId;
}

interface Video extends AudioClipOffsets {
  source: string | File;
  uploadedVideoId?: number;
}

export interface ExportConfiguration extends TemplateEditorStateExport {
  aspectRatio: AspectRatioDimensions;
  audio?: Audio | ClippedAudio;
  fullEpisodeExport?: boolean;
  podcastIdentifier?: PodcastIdentifier;
  video?: Video;
  projectName?: string;
  initiateExport: boolean;
  projectCreatMethod?: ProjectCreationMethod;
  templateId: string | 'none';
  transcription: Transcription;
  customTraceId?: string;
  traceId?: string;
  expectedDurationMilli?: number;
  frameSizeOverride?: FrameSize;
  isAutoFrame?: boolean;
  isClipSuggestion?: boolean;
  clipSuggestionLengthSecs?: number;
  enableBlur?: boolean;
  isVideoConfigLocked?: boolean;
  useYoutubeMostReplayed?: boolean;
  uploadedRecordingId?: number;
  captionSourceEddyProjectCuid?: string;
}

export type EddyProjectVisibility = 'visible' | 'hidden';

export type CreateCreationAssetConfig = CreateCreationAssetArgs & {
  eddyProjectVisibility?: EddyProjectVisibility;
  podcastIdentifier?: PodcastIdentifier;
  soundwave?: Soundwave;
};

export interface State {
  creationId: number;
  status: RequestStatus;
  pollStatus: RequestStatus;
  createCreationAssetStatus: RequestStatus | undefined;
  uploadedRecordingId: number | undefined;
  captionSourceEddyProjectCuid: string | undefined;
}

export type WizardExportState = RecordOf<State>;

type CreationRequestAction = Action<Type.CREATION_REQUEST>;
type CreationSuccessAction = Action<
  Type.CREATION_SUCCESS,
  { creationId: number }
>;
type CreationFailureAction = Action<Type.CREATION_FAILURE>;
type ClearCreationAction = Action<Type.CREATION_CLEAR>;

type CreationPollRequestAction = Action<Type.CREATION_STATUS_POLL_REQUEST>;
type CreationPollSuccessAction = Action<Type.CREATION_STATUS_POLL_SUCCESS>;
type CreationPollFailureAction = Action<Type.CREATION_STATUS_POLL_FAILURE>;

type CreateCreationAssetRequestAction = Action<
  Type.CREATE_CREATION_ASSET_REQUEST
>;
type CreateCreationAssetSuccessAction = Action<
  Type.CREATE_CREATION_ASSET_SUCCESS,
  {
    uploadedRecordingId: number;
    captionSourceEddyProjectCuid: string;
  }
>;
type CreateCreationAssetFailureAction = Action<
  Type.CREATE_CREATION_ASSET_FAILURE
>;
type ClearCreateCreationAssetAction = Action<Type.CREATE_CREATION_ASSET_CLEAR>;

export type WizardExportAction =
  | ClearCreationAction
  | CreationRequestAction
  | CreationSuccessAction
  | CreationFailureAction
  | CreationPollRequestAction
  | CreationPollSuccessAction
  | CreationPollFailureAction
  | CreateCreationAssetRequestAction
  | CreateCreationAssetSuccessAction
  | CreateCreationAssetFailureAction
  | ClearCreateCreationAssetAction;
